<script lang="ts">
	import { scale } from 'svelte/transition';
	import { closeModal } from '$lib/modal-store';
	import Spinner from '../smol_ui/spinner.svelte';
	import { verifyMfa } from '$lib/account.svelte.js';
	import { toastDisplay } from '$lib/utils.svelte.js';
	import { t } from '$lib/i18n.svelte.js';

	let { token = '', secret = '', otpauth = '', setup = false, disable = false, onSuccess = () => {} } = $props();
	
	let digits = $state(['', '', '', '', '', '']);
	let code = $derived(digits.join(''));
	let loading = $state(false);
	let errorMsg = $state('');
	let hiddenInput: HTMLInputElement;

	function fillDigits(value: string) {
		const cleanValue = value.replace(/\D/g, '').slice(0, 6);
		const newDigits = [...cleanValue.split(''), ...Array(6).fill('')].slice(0, 6);
		digits = newDigits;
		
		// Focus the next empty input or last input
		const nextIndex = Math.min(cleanValue.length, 5);
		setTimeout(() => document.getElementById(`d${nextIndex}`)?.focus(), 0);
	}

	function handleInput(i: number, e: Event & { currentTarget: HTMLInputElement }) {
		const val = e.currentTarget.value;
		
		// Handle multiple characters (paste or autofill)
		if (val.length > 1) {
			fillDigits(val);
			return;
		}
		
		// Handle single character
		const cleanVal = val.slice(-1).replace(/\D/g, '');
		digits[i] = cleanVal;
		if (cleanVal && i < 5) document.getElementById(`d${i + 1}`)?.focus();
	}

	function handlePaste(e: ClipboardEvent) {
		e.preventDefault();
		const pastedData = e.clipboardData?.getData('text') || '';
		fillDigits(pastedData);
	}

	function handleKeydown(i: number, e: KeyboardEvent) {
		if (e.key === 'Backspace' && !digits[i] && i > 0) {
			document.getElementById(`d${i - 1}`)?.focus();
		} else if (e.key === 'ArrowLeft' && i > 0) {
			document.getElementById(`d${i - 1}`)?.focus();
		} else if (e.key === 'ArrowRight' && i < 5) {
			document.getElementById(`d${i + 1}`)?.focus();
		} else if (e.key === 'Enter' && code.length === 6) submit();
	}

	// Handle password manager autofill
	function handleHiddenInput(e: Event & { currentTarget: HTMLInputElement }) {
		const value = e.currentTarget.value;
		if (value) {
			fillDigits(value);
			hiddenInput.value = ''; // Clear the hidden input
		}
	}

	async function submit() {
		if (loading || code.length !== 6) return;
		loading = true;
		errorMsg = '';
		try {
			if (disable) {
				// For disable flow, call the onSuccess callback with the code
				await onSuccess(code);
			} else {
				// For setup/login flow, handle verification directly
				await verifyMfa({ code, token, secret });
				toastDisplay(setup ? t('user.mfaEnabled') : t('user.successLogin'), 'success');
				onSuccess();
			}
			closeModal();
		} catch {
			errorMsg = disable ? t('error.invalidMfaCode') : t('error.invalidMfaCode');
			// Clear all fields on error
			digits = ['', '', '', '', '', ''];
			// Focus first input
			setTimeout(() => document.getElementById('d0')?.focus(), 100);
		} finally {
			loading = false;
		}
	}

	// Auto-submit when code is complete
	$effect(() => {
		if (code.length === 6 && !loading) {
			setTimeout(() => submit(), 50);
		}
	});
</script>

<div class="fixed inset-0 flex items-center justify-center">
	<div class="bg-white dark:bg-gray-800 rounded-2xl p-6 w-96 shadow-xl border border-gray-200 dark:border-gray-700 pointer-events-auto" transition:scale={{ duration: 200, start:0.9 }}>
		{#if setup}
			<div class="text-center mb-6">
				<h2 class="text-lg font-semibold mb-2 font-rethink">{t('user.enableMfa')}</h2>
				{#if otpauth}
					<img class="mx-auto mb-4 p-8 py-4 w-full" src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data={encodeURIComponent(otpauth)}" alt="QR" />
				{/if}
				{#if secret}
					<div class="font-mono text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded mb-4">{secret}</div>
				{/if}
			</div>
		{:else}
			<div class="text-center mb-6">
				<h2 class="text-lg font-semibold font-rethink">{disable ? t('user.disableMfa') : t('user.multifactorAuth')}</h2>
				{#if !disable}
					<p class="text-sm text-gray-600 dark:text-gray-400 mt-2">{t('user.mfaVerificationInfo')}</p>
				{/if}
			</div>
		{/if}
		
		<div class="text-center mb-6">
			<!-- Hidden input for password managers -->
			<input
				bind:this={hiddenInput}
				type="text"
				autocomplete="one-time-code"
				class="absolute opacity-0 pointer-events-none"
				tabindex="-1"
				oninput={handleHiddenInput}
			/>
			
			<div class="flex gap-2 justify-center">
				{#each digits as digit, i}
					<input
						id="d{i}"
						class="w-12 h-12 text-center text-2xl font-bold rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all outline-none {loading ? 'opacity-50' : ''}"
						type="text"
						inputmode="numeric"
						autocomplete="off"
						maxlength="6"
						value={digit}
						disabled={loading}
						oninput={(e) => handleInput(i, e)}
						onkeydown={(e) => handleKeydown(i, e)}
						onpaste={handlePaste}
						transition:scale={{ duration: 150, delay: i * 20 }}
					/>
				{/each}
			</div>
		</div>
		
		{#if errorMsg}
			<div class="text-sm text-red-500 text-center mb-4">
				{errorMsg}
			</div>
		{:else if loading && code.length === 6}
			<div class="text-sm text-gray-500 text-center mb-4 flex items-center justify-center gap-2">
				<Spinner size={3} />
				Verifying...
			</div>
		{/if}
		
		<button
			class="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center"
			disabled={loading || code.length !== 6}
			onclick={submit}
		>
			{#if loading}<Spinner size={4} />{:else}{t('info.confirm')}{/if}
		</button>
	</div>
</div>
